/** @jsx jsx */
import { useCallback, useState } from 'react';
import { css, jsx } from '@emotion/react';
import { FormattedMessage } from 'react-intl';
import { BrowserSizeMode } from './types/common';
import { getAppStore } from './store';
import { getModuleSync } from './module-loader';
import defaultI18nMessages from './translations/default';
import { SessionManager } from './session-manager';
import { getStandardPortalUrl } from './utils/portal-url-utils';
import downFilledSvg from 'jimu-icons/svg/filled/directional/down.svg';
import rightFilledSvg from 'jimu-icons/svg/filled/directional/right.svg';
import warningSvg from 'jimu-icons/svg/outlined/suggested/warning.svg';
import { getServerRootUrl, isServerUrl } from './utils/service-url-utils';
function getStyle(theme) {
    return css `
    .permission-banner-list {
      position: fixed;
      bottom: 2px;
      right: 2px;
      alignItems: stretch;
      flexDirection: column;
      z-index: 999;
      fontSize: 13px;
      .banner-list-panel {
        color: var(--black, #FFF);
        margin-left: 27px;
      }
      .warning-icon {
        position: absolute;
        left: 10px;
        width: 24px;
        height: 24px;
        color: var(--sys-color-warning-dark) !important;
      }
      .banner-list-item {
      }
      .banner-list-item-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1px;
        position: relative;
        .item-info {
          flex-grow: 2;
          .fold-btn {
            position: absolute;
            left: -22px;
            .directional {
              width: 8px;
              height: 8px;
              color: var(--ref-palette-neutral-1000) !important;
            }
          }
        }
        .item-link {
          display: block;
          color: var(--sys-color-primary-dark);
          max-width: 160px;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
        }
        .item-link.sub-item-link {
          max-width: 315px;
        }
        .item-operation {
          display: flex;
          align-items: center;
        }

        .button-separator {
          height: 18px;
          border-right-width: 1px;
          border-right-style: solid;
          border-right-color: var(--ref-palette-neutral-700)
        }
      }
      .user-name {
        max-width: 315px;
        font-size: 12px;
        font-style: italic;
        color: var(--ref-palette-neutral-900);
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      }
      .jimu-btn {
        color: var(--ref-palette-neutral-1000)
      }
      .jimu-btn: hover {
        background-color: transparent;
        color: var(--sys-color-primary-light);
      }
      .ignore-all-btn {
        border: 1px solid var(--ref-palette-neutral-500);
        width: 100%;
        height: 26px;
        margin-top: 10px;
      }
      .left-part {
        display: block !important
      }
    }`;
}
function getBannerList() {
    const bannerList = {};
    const sessionManager = SessionManager.getInstance();
    const noPermissionResourceInfoList = SessionManager.getInstance().getNoPermissionResourceInfoList();
    Object.entries(noPermissionResourceInfoList).forEach(entry => {
        const resourceUrl = entry[0];
        const noPermissionResourceInfo = entry[1];
        if (!noPermissionResourceInfo.ignored) {
            const isServiceUrl = !!sessionManager.getServerKeyFromUrl(resourceUrl);
            let rootUrl;
            if (isServiceUrl) {
                if (noPermissionResourceInfo.owningSystemUrl) {
                    rootUrl = getStandardPortalUrl(noPermissionResourceInfo.owningSystemUrl);
                }
                else {
                    rootUrl = getServerRootUrl(resourceUrl);
                }
            }
            else {
                rootUrl = getStandardPortalUrl(resourceUrl);
            }
            if (!bannerList[rootUrl]) {
                bannerList[rootUrl] = [];
            }
            if (rootUrl !== resourceUrl) {
                bannerList[rootUrl].push(resourceUrl);
            }
        }
    });
    return bannerList;
}
export function PermissionBannerList(props) {
    const [unFoldFlags, setIsUnFold] = useState({});
    const handleIgnoreAll = useCallback(() => {
        SessionManager.getInstance().ignoreFromNoPermissionResourceInfoList();
    }, []);
    const handleFoldBtnClick = useCallback((isUnFold, url) => {
        const newUnFoldFlags = Object.assign({}, unFoldFlags);
        newUnFoldFlags[url] = isUnFold;
        setIsUnFold(newUnFoldFlags);
    }, [unFoldFlags]);
    const { Alert, Button, Icon } = getModuleSync('jimu-ui');
    const { theme, intl } = props;
    const bannerList = getBannerList();
    const isSmallSize = getAppStore().getState().browserSizeMode === BrowserSizeMode.Small;
    const width = isSmallSize ? 'auto' : '400px';
    const left = isSmallSize ? '2px' : 'auto';
    return (jsx("div", { css: getStyle(theme) },
        jsx(Alert, { form: 'basic', type: 'info', open: Object.keys(bannerList).length > 0, className: 'permission-banner-list p-4', closable: false, withIcon: false, style: {
                left,
                width,
                borderColor: theme.ref.palette.neutral[500],
                backgroundColor: theme.ref.palette.neutral[400]
            } },
            jsx("div", { className: 'banner-list-panel' },
                jsx(Icon, { className: 'warning-icon', icon: warningSvg }),
                jsx(FormattedMessage, { id: 'signInBannerListWarning', defaultMessage: defaultI18nMessages.signInBannerListWarning }),
                jsx("div", { className: 'mt-4' }, Object.entries(bannerList).map((entry, index) => {
                    const rootUrl = entry[0];
                    const subUrls = entry[1];
                    return (jsx("div", { key: index },
                        jsx(BannerListItem, { url: rootUrl, resourceUrl: subUrls[0], theme: theme, intl: intl, key: index, hasSubItems: subUrls.length > 0, isUnFold: unFoldFlags[rootUrl], foldBtnClick: handleFoldBtnClick }),
                        unFoldFlags[rootUrl] && subUrls.map((subUrl, index) => {
                            return jsx(BannerListSubItem, { url: subUrl, theme: theme, intl: intl, key: index });
                        })));
                })),
                Button && jsx(Button, { className: 'ignore-all-btn', type: 'tertiary', size: 'sm', onClick: handleIgnoreAll }, props.intl.formatMessage({ id: 'signInBannerIgnoreAll', defaultMessage: defaultI18nMessages.signInBannerIgnoreAll }))))));
}
function BannerListItem(props) {
    var _a, _b;
    const { url, resourceUrl, theme, intl, hasSubItems, isUnFold, foldBtnClick } = props;
    const handleLogin = useCallback(() => {
        const sessionManager = SessionManager.getInstance();
        let signInUrl;
        if (isServerUrl(resourceUrl) && getServerRootUrl(resourceUrl) === url) {
            // non-federated service, only use service url to sign-in
            signInUrl = resourceUrl;
        }
        else {
            signInUrl = url;
        }
        sessionManager.signInByResourceUrl(signInUrl, null, true);
    }, [url, resourceUrl]);
    const handleIgnore = useCallback(() => {
        SessionManager.getInstance().ignoreFromNoPermissionResourceInfoList(url);
    }, [url]);
    const handleFold = useCallback(() => {
        foldBtnClick(!isUnFold, url);
    }, [url, isUnFold, foldBtnClick]);
    const { Button, Icon } = getModuleSync('jimu-ui');
    const userName = ((_a = SessionManager.getInstance().getSessionByUrl(url)) === null || _a === void 0 ? void 0 : _a.username) || ((_b = SessionManager.getInstance().getSessionByUrl(`${url}/rest/services`)) === null || _b === void 0 ? void 0 : _b.username);
    const currentStr = intl.formatMessage({ id: 'currentData', defaultMessage: 'current' });
    const signInOrSwitchStr = userName ? intl.formatMessage({ id: 'switch', defaultMessage: 'Switch' }) : intl.formatMessage({ id: 'signIn', defaultMessage: 'Sign in' });
    const ignoreStr = intl.formatMessage({ id: 'signInBannerIgnore', defaultMessage: defaultI18nMessages.signInBannerIgnore });
    return (jsx("div", { className: 'banner-list-item' },
        jsx("div", { className: 'banner-list-item-content', css: getStyle(theme) },
            jsx("div", { className: 'item-info' },
                jsx("div", null,
                    hasSubItems && Button && jsx(Button, { className: 'fold-btn', size: 'sm', type: 'tertiary', icon: true, onClick: handleFold }, isUnFold ? jsx(Icon, { className: 'directional', icon: downFilledSvg }) : jsx(Icon, { className: 'directional', icon: rightFilledSvg })),
                    jsx("a", { className: 'item-link', href: url, target: '_blank', rel: "noopener noreferrer", title: url }, url))),
            jsx("div", { className: 'item-operation' },
                Button && jsx(Button, { size: 'sm', type: 'tertiary', title: signInOrSwitchStr, onClick: handleLogin },
                    jsx("div", { className: 'text-truncate' }, signInOrSwitchStr)),
                jsx("div", { className: 'button-separator', style: {} }),
                Button && jsx(Button, { size: 'sm', type: 'tertiary', title: ignoreStr, onClick: handleIgnore },
                    jsx("div", { className: 'text-truncate' }, ignoreStr)))),
        userName && jsx("div", { className: 'user-name' }, `${currentStr}:${userName}`)));
}
function BannerListSubItem(props) {
    const { url, theme } = props;
    return (jsx("div", { className: 'banner-list-item-content', css: getStyle(theme) },
        jsx("a", { className: 'item-link sub-item-link', href: url, target: '_blank', rel: "noopener noreferrer", title: url }, url)));
}
